/**
 * QB Promo - shared state + behaviour for the promo banner and popup
 * (sections/qb-promo.liquid). One module drives both surfaces so they
 * share a single suppression state.
 *
 * State model (one localStorage key, mirrored to a customer metafield by
 * the account-preferences card when that write path ships):
 *   {
 *     dismissedAll: bool,          // "don't show again" -> no popups, ever
 *     subscribed: bool,            // signed up -> newsletter popups stop
 *     codeUsed: bool,              // best-effort; authoritative when logged in
 *     seen: { [campaignId]: s },   // popup shown at epoch s -> frequency cap
 *     bannerDismissed: { [campaignId]: s }
 *   }
 *
 * Anonymous visitors only have localStorage (per device, cleared with
 * cookies) - that is best-effort by design. When a customer is logged in,
 * the section renders their marketing consent and their qbtools.popup_prefs
 * metafield into data attributes, and those win over localStorage.
 */
(function () {
  'use strict';

  var KEY = 'qb_promo';

  function readState() {
    try {
      var s = JSON.parse(localStorage.getItem(KEY)) || {};
      s.seen = s.seen || {};
      s.bannerDismissed = s.bannerDismissed || {};
      return s;
    } catch (e) {
      return { seen: {}, bannerDismissed: {} };
    }
  }
  function writeState(s) {
    try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) { /* private mode */ }
  }

  function init() {
    var root = document.getElementById('qbPromo');
    if (!root) return;

    var cfgEl = root.querySelector('[data-qb-promo-config]');
    if (!cfgEl) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

    var designMode = window.Shopify && window.Shopify.designMode;
    var state = readState();

    /* ---- logged-in customer data is authoritative over localStorage ---- */
    var loggedIn = root.getAttribute('data-logged-in') === 'true';
    if (loggedIn) {
      if (root.getAttribute('data-subscribed') === 'true') state.subscribed = true;
      var prefsRaw = root.getAttribute('data-prefs');
      if (prefsRaw) {
        try {
          var prefs = JSON.parse(prefsRaw);
          if (prefs && typeof prefs === 'object') {
            if (prefs.hidePopups === true) state.dismissedAll = true;
            if (prefs.hidePopups === false) state.dismissedAll = false;
            if (prefs.codeUsed === true) state.codeUsed = true;
          }
        } catch (e) { /* malformed metafield: ignore */ }
      }
      writeState(state);
    }

    /* Campaign windows were converted to epoch seconds by Liquid in the
       SHOP's timezone, so the client only ever compares absolute times.
       serverNow guards against a client clock that is far behind; page
       caching can only make serverNow stale (old), so taking the max can
       never fire a scheduled campaign early. */
    function now() {
      return Math.max(Math.floor(Date.now() / 1000), cfg.serverNow || 0);
    }

    function live(c) {
      if (!c.enabled) return false;
      var t = now();
      if (c.start && t < c.start) return false;
      if (c.end && t > c.end) return false;
      return true;
    }

    /* Highest priority wins; ties resolve to editor order (stable sort). */
    function pick(surface) {
      var list = cfg.campaigns.filter(function (c) {
        return live(c) && (c.surface === surface || c.surface === 'both');
      });
      list.sort(function (a, b) { return b.priority - a.priority; });
      return list[0] || null;
    }

    /* ================= BANNER ================= */
    var banner = document.getElementById('qbPromoBanner');

    function setPromoH(px) {
      document.documentElement.style.setProperty('--promo-h', px + 'px');
    }

    function fillBanner(c) {
      var set = function (sel, txt) {
        var el = banner.querySelector(sel);
        if (!el) return;
        if (txt) { el.textContent = txt; el.hidden = false; } else { el.hidden = true; }
      };
      set('[data-pb-heading]', c.heading);
      set('[data-pb-body]', c.body);
      /* newsletter codes are only revealed after signup, never on a banner */
      set('[data-pb-code]', c.type === 'newsletter' ? '' : c.code);
      var cta = banner.querySelector('[data-pb-cta]');
      if (cta) {
        if (c.ctaLabel && c.ctaLink) {
          cta.textContent = c.ctaLabel + ' →';
          cta.href = c.ctaLink;
          cta.hidden = false;
        } else if (c.ctaLabel && c.type === 'newsletter') {
          /* a newsletter banner with no link opens the signup popup */
          cta.textContent = c.ctaLabel + ' →';
          cta.href = '#';
          cta.hidden = false;
          cta.addEventListener('click', function (e) {
            e.preventDefault();
            var target = pick('popup') || c;
            fillPopup(target);
            state.seen[target.id] = now();
            writeState(state);
            openPopup();
          });
        } else { cta.hidden = true; }
      }
    }

    var measureQueued = false;
    function measureBanner() {
      if (measureQueued) return;
      measureQueued = true;
      requestAnimationFrame(function () {
        measureQueued = false;
        if (!banner || banner.hidden) return;
        setPromoH(banner.offsetHeight);
      });
    }

    function initBanner() {
      if (!banner) return;
      var c = pick('banner');
      var dismissed = c && state.bannerDismissed[c.id];
      if (!c || (dismissed && !designMode)) {
        banner.hidden = true;
        setPromoH(0);
        return;
      }
      fillBanner(c);
      banner.hidden = false;
      measureBanner();
      window.addEventListener('resize', measureBanner, { passive: true });

      var x = banner.querySelector('[data-pb-dismiss]');
      if (x) {
        x.addEventListener('click', function () {
          state.bannerDismissed[c.id] = now();
          writeState(state);
          banner.hidden = true;
          setPromoH(0);
        });
      }
    }

    /* ================= POPUP ================= */
    var pop = document.getElementById('qbPromoPop');
    var lastFocus = null;
    var popCampaign = null;

    function popupSuppressed(c) {
      if (designMode) return false;
      if (state.dismissedAll) return true;
      if (c.type === 'newsletter' && state.subscribed) return true;
      if (c.code && state.codeUsed) return true;
      var seenAt = state.seen[c.id];
      if (seenAt && now() - seenAt < cfg.frequencyDays * 86400) return true;
      return false;
    }

    function fillPopup(c) {
      var set = function (sel, txt) {
        var el = pop.querySelector(sel);
        if (!el) return;
        if (txt) { el.textContent = txt; el.hidden = false; } else { el.hidden = true; }
      };
      set('[data-pp-kicker]', c.name);
      set('[data-pp-heading]', c.heading);
      set('[data-pp-body]', c.body);
      set('[data-pp-code]', c.type === 'newsletter' ? '' : c.code);
      var imgWrap = pop.querySelector('[data-pp-img]');
      if (imgWrap) {
        var img = imgWrap.querySelector('img');
        if (c.image && img) { img.src = c.image; imgWrap.hidden = false; }
        else { imgWrap.hidden = true; if (img) img.removeAttribute('src'); }
      }
      var cta = pop.querySelector('[data-pp-cta]');
      if (cta) {
        if (c.ctaLabel && c.ctaLink) {
          cta.textContent = c.ctaLabel;
          cta.href = c.ctaLink;
          cta.hidden = false;
        } else { cta.hidden = true; }
      }
      var nl = pop.querySelector('[data-pp-nl]');
      if (nl) nl.hidden = c.type !== 'newsletter';
    }

    function openPopup() {
      if (!pop || !pop.hidden) return;
      lastFocus = document.activeElement;
      pop.hidden = false;
      requestAnimationFrame(function () { pop.classList.add('open'); });
      var card = pop.querySelector('.qb-promo-pop__card');
      if (card) card.focus();
      document.addEventListener('keydown', onKey);
    }

    function closePopup() {
      if (!pop || pop.hidden) return;
      var never = pop.querySelector('[data-pp-never]');
      if (never && never.checked) {
        state.dismissedAll = true;
        writeState(state);
      }
      pop.classList.remove('open');
      pop.hidden = true;
      document.removeEventListener('keydown', onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function onKey(e) {
      if (e.key === 'Escape') closePopup();
    }

    function armPopup() {
      if (!pop) return;

      /* A completed signup re-renders the popup with the success panel
         (and the discount code) server-side. Show it immediately, and
         remember the signup so newsletter popups stop. */
      if (pop.querySelector('[data-pp-success]')) {
        state.subscribed = true;
        writeState(state);
        ['[data-pp-kicker]', '[data-pp-heading]', '[data-pp-body]', '[data-pp-code]', '[data-pp-cta]']
          .forEach(function (sel) {
            var el = pop.querySelector('.qb-promo-pop__card > ' + sel);
            if (el) el.hidden = true;
          });
        pop.querySelector('[data-pp-nl]').hidden = false;
        openPopup();
        return;
      }
      /* A failed signup re-renders with the error text - reopen the form
         so the visitor sees why. */
      if (pop.querySelector('[data-pp-error]')) {
        var errCampaign = pick('popup');
        if (errCampaign) fillPopup(errCampaign);
        pop.querySelector('[data-pp-nl]').hidden = false;
        openPopup();
        return;
      }

      popCampaign = pick('popup');
      if (!popCampaign || popupSuppressed(popCampaign)) return;
      fillPopup(popCampaign);

      var fired = false;
      function fire() {
        if (fired) return;
        fired = true;
        clearTimeout(timer);
        window.removeEventListener('scroll', onScroll);
        state.seen[popCampaign.id] = now();
        writeState(state);
        openPopup();
      }
      /* whichever happens first: the delay, or scrolling deep enough */
      var timer = setTimeout(fire, (cfg.delay || 0) * 1000);
      function onScroll() {
        var doc = document.documentElement;
        var max = doc.scrollHeight - window.innerHeight;
        if (max <= 0) return;
        if ((window.scrollY / max) * 100 >= cfg.scrollDepth) fire();
      }
      if (cfg.scrollDepth > 0) window.addEventListener('scroll', onScroll, { passive: true });
    }

    if (pop) {
      pop.addEventListener('click', function (e) {
        if (e.target.closest('[data-pp-close]')) closePopup();
        var chip = e.target.closest('[data-code-copy]');
        if (chip && navigator.clipboard) {
          navigator.clipboard.writeText(chip.textContent.trim()).then(function () {
            var note = pop.querySelector('[data-copy-note]');
            if (note) note.textContent = 'Copied to clipboard';
          }).catch(function () { /* clipboard blocked: the code is still readable */ });
        }
      });
    }

    initBanner();
    if (!designMode) armPopup();

    /* Theme editor: selecting the section previews the popup. */
    if (designMode) {
      document.addEventListener('shopify:section:select', function (e) {
        if (root.closest('.shopify-section') === e.target) {
          var c = pick('popup');
          if (c) { fillPopup(c); openPopup(); }
        }
      });
      document.addEventListener('shopify:section:deselect', function (e) {
        if (root.closest('.shopify-section') === e.target) closePopup();
      });
    }

    /* Shared with the account-preferences card (and anything else). */
    window.QBPromo = {
      getState: readState,
      setState: function (patch) {
        var s = readState();
        Object.keys(patch).forEach(function (k) { s[k] = patch[k]; });
        writeState(s);
        return s;
      },
      openPopup: openPopup
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor reloads the section's HTML in place. */
  document.addEventListener('shopify:section:load', function () {
    if (document.getElementById('qbPromo') && !window.QBPromo) init();
  });
})();
